import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { createLogger } from '../utils/logger'
import { updateExperimentsCache } from '../utils/analytics/core'

const logger = createLogger('AppConfig')
const AppConfigContext = createContext()

// Default configuration
const defaultConfig = {
  apiUrl: import.meta.env.VITE_API_URL || '/api',
  appName: 'TrckIt',
  version: '1.0.0',
  environment: import.meta.env.MODE || 'development',
  features: {
    notifications: true,
    realTimeTracking: true,
    googleAuth: true,
    mapIntegration: true
  },
  map: {
    defaultCenter: [40.7128, -74.0060], // New York City
    defaultZoom: 12,
    maxZoom: 18,
    minZoom: 3,
    provider: 'OpenStreetMap'
  },
  search: {
    defaultRadius: 10, // kilometers
    maxRadius: 50,
    minRadius: 1
  },
  pagination: {
    defaultLimit: 20,
    maxLimit: 100
  },
  upload: {
    maxFileSize: 5 * 1024 * 1024, // 5MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif'],
    maxFiles: 5
  },
  cache: {
    ttl: 300000, // 5 minutes in milliseconds
    maxEntries: 1000
  }
}

// Simple djb2 hash for deterministic variant assignment
const hashString = (str) => {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i)
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash)
}

export const AppConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(defaultConfig)
  const [isLoading, setIsLoading] = useState(false)

  // Track active experiment assignments (experimentId -> variant)
  const [activeExperiments, setActiveExperiments] = useState(() => {
    const experiments = {}
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith('ab_')) {
          experiments[key.slice(3)] = localStorage.getItem(key)
        }
      }
    } catch (e) {
      // localStorage may be unavailable
    }
    return experiments
  })

  // Load remote configuration if available
  useEffect(() => {
    const loadRemoteConfig = async () => {
      if (import.meta.env.VITE_REMOTE_CONFIG_URL) {
        setIsLoading(true)
        try {
          const response = await fetch(import.meta.env.VITE_REMOTE_CONFIG_URL)
          const remoteConfig = await response.json()
          
          // Merge remote config with defaults
          setConfig(prevConfig => ({
            ...prevConfig,
            ...remoteConfig
          }))
        } catch (error) {
          logger.warn('Failed to load remote configuration, using defaults', error)
        } finally {
          setIsLoading(false)
        }
      }
    }

    loadRemoteConfig()
  }, [])

  // Sync active experiments to analytics module-level cache
  useEffect(() => {
    updateExperimentsCache(activeExperiments)
  }, [activeExperiments])

  // Update configuration
  const updateConfig = (newConfig) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      ...newConfig
    }))
  }

  // Get nested configuration value
  const getConfigValue = (path, defaultValue = null) => {
    const keys = path.split('.')
    let value = config
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key]
      } else {
        return defaultValue
      }
    }
    
    return value
  }

  // Check if feature is enabled
  const isFeatureEnabled = (featureName) => {
    return getConfigValue(`features.${featureName}`, false)
  }

  // Get variant assignment for an A/B experiment (sticky, deterministic)
  // variants: array of names, e.g. ['control', 'variant_a']
  // userId: optional — enables deterministic bucketing; anonymous uses session-stable random
  const getVariant = useCallback((experimentId, variants, userId = null) => {
    if (!variants || variants.length === 0) return null

    const storageKey = `ab_${experimentId}`

    // Return sticky assignment if already set for this browser
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored && variants.includes(stored)) return stored
    } catch (e) {
      // localStorage unavailable
    }

    // Assign bucket
    let bucketIndex
    if (userId) {
      bucketIndex = hashString(`${userId}_${experimentId}`) % variants.length
    } else {
      try {
        const sessionKey = `ab_anon_${experimentId}`
        const sessionStored = sessionStorage.getItem(sessionKey)
        if (sessionStored !== null) {
          bucketIndex = parseInt(sessionStored, 10)
        } else {
          bucketIndex = Math.floor(Math.random() * variants.length)
          sessionStorage.setItem(sessionKey, String(bucketIndex))
        }
      } catch (e) {
        bucketIndex = Math.floor(Math.random() * variants.length)
      }
    }

    const variant = variants[bucketIndex]

    try {
      localStorage.setItem(storageKey, variant)
    } catch (e) {
      // localStorage unavailable
    }

    // Defer state update so getVariant is safe to call during render
    setTimeout(() => {
      setActiveExperiments(prev => {
        if (prev[experimentId] === variant) return prev
        return { ...prev, [experimentId]: variant }
      })
    }, 0)
    logger.debug('Assigned variant', { experimentId, variant, userId: userId || 'anonymous' })
    return variant
  }, [])

  // Get API endpoint
  const getApiEndpoint = (endpoint) => {
    return `${config.apiUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`
  }

  const value = {
    config,
    isLoading,
    updateConfig,
    getConfigValue,
    isFeatureEnabled,
    getApiEndpoint,
    getVariant,
    activeExperiments
  }

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  )
}

export const useAppConfig = () => {
  const context = useContext(AppConfigContext)
  if (!context) {
    throw new Error('useAppConfig must be used within an AppConfigProvider')
  }
  return context
}

export default AppConfigContext